/**
 * AxLabs Mathematics Experiments - Discrete Mathematics
 *
 * Collection of experiments exploring discrete math concepts:
 * counting principles, permutations, combinations, recurrence relations,
 * pigeonhole principle, inclusion-exclusion, and Boolean algebra.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState, factorial, binomial } from './helpers.js';
// ============================================================================
// COUNTING PERMUTATIONS
// ============================================================================
export const CountingPermutations = createExperimentFromSchema({
    id: 'counting-permutations',
    name: 'Counting Permutations',
    domain: 'mathematics',
    description: 'Explore permutations and arrangements (n!)',
    conceptTags: ['Permutations', 'Factorial', 'Counting', 'Arrangements'],
    difficulty: 3,
    stateVariables: [
        { id: 'n', name: 'Items n', unit: 'dimensionless', initial: 5, symbol: 'n' },
        { id: 'r', name: 'Arrange r', unit: 'dimensionless', initial: 3, symbol: 'r' },
        { id: 'permutations', name: 'P(n,r)', unit: 'dimensionless', initial: 60, symbol: 'P(n,r)' },
        { id: 'factorial', name: 'n!', unit: 'dimensionless', initial: 120, symbol: 'n!' },
        { id: 'ratio', name: 'P(n,r) / n!', unit: 'dimensionless', initial: 0.5, symbol: '\\rho' },
    ],
    parameters: [
        { id: 'nTarget', name: 'n Items', unit: 'dimensionless', min: 1, max: 10, default: 5, symbol: 'n' },
        { id: 'rTarget', name: 'r Position', unit: 'dimensionless', min: 1, max: 10, default: 3, symbol: 'r' },
    ],
    equations: [
        {
            id: 'permutation-formula',
            name: 'Permutation Formula',
            latex: 'P(n,r) = \\frac{n!}{(n-r)!}',
            description: 'Number of ways to arrange r items from n',
        },
        {
            id: 'factorial-def',
            name: 'Factorial',
            latex: 'n! = n \\times (n-1) \\times \\cdots \\times 1',
            description: 'Product of all positive integers up to n',
        },
    ],
    derivatives: (_state, params) => {
        const nTarget = Math.round(getParam(params, 'nTarget'));
        const rTarget = Math.round(getParam(params, 'rTarget'));
        const clampedR = Math.min(rTarget, nTarget);
        const nFact = factorial(nTarget);
        const nrFact = factorial(Math.max(0, nTarget - clampedR));
        const permutations = nFact / nrFact;
        const ratio = nFact > 0 ? permutations / nFact : 0;
        return {
            n: nTarget - getState(_state, 'n'),
            r: clampedR - getState(_state, 'r'),
            permutations: permutations - getState(_state, 'permutations'),
            factorial: nFact - getState(_state, 'factorial'),
            ratio: ratio - getState(_state, 'ratio'),
        };
    },
    bounds: {
        n: { min: 1, max: 10 },
        permutations: { min: 1, max: 3.6e6 },
    },
});
// ============================================================================
// COMBINATION SELECTION
// ============================================================================
export const CombinationSelection = createExperimentFromSchema({
    id: 'combination-selection',
    name: 'Combination Selection',
    domain: 'mathematics',
    description: 'Explore combinations and binomial coefficients C(n,k)',
    conceptTags: ['Combinations', 'Binomial Coefficients', 'Pascal Triangle', 'Counting'],
    difficulty: 3,
    stateVariables: [
        { id: 'n', name: 'Total n', unit: 'dimensionless', initial: 10, symbol: 'n' },
        { id: 'k', name: 'Choose k', unit: 'dimensionless', initial: 3, symbol: 'k' },
        { id: 'combinations', name: 'C(n,k)', unit: 'dimensionless', initial: 120, symbol: '\\binom{n}{k}' },
        { id: 'pascalRow', name: 'Pascal Row Sum', unit: 'dimensionless', initial: 512, symbol: '\\sum' },
        { id: 'symmetry', name: 'C(n,k) = C(n,n-k)', unit: 'dimensionless', initial: 1, symbol: 'Sym' },
    ],
    parameters: [
        { id: 'nTarget', name: 'n Total', unit: 'dimensionless', min: 1, max: 15, default: 10, symbol: 'n' },
        { id: 'kTarget', name: 'k Choose', unit: 'dimensionless', min: 0, max: 15, default: 3, symbol: 'k' },
    ],
    equations: [
        {
            id: 'combination-formula',
            name: 'Combination Formula',
            latex: '\\binom{n}{k} = \\frac{n!}{k!(n-k)!}',
            description: 'Number of ways to choose k items from n (order doesn\'t matter)',
        },
        {
            id: 'binomial-theorem',
            name: 'Binomial Theorem',
            latex: '(a+b)^n = \\sum_{k=0}^n \\binom{n}{k}a^{n-k}b^k',
            description: 'Binomial coefficients appear in expansion',
        },
        {
            id: 'symmetry',
            name: 'Symmetry Property',
            latex: '\\binom{n}{k} = \\binom{n}{n-k}',
            description: 'Choosing k is same as choosing n-k',
        },
    ],
    derivatives: (_state, params) => {
        const nTarget = Math.round(getParam(params, 'nTarget'));
        const kTarget = Math.round(getParam(params, 'kTarget'));
        const clampedK = Math.min(kTarget, nTarget);
        const combinations = binomial(nTarget, clampedK);
        const pascalRow = Math.pow(2, nTarget);
        const symmetry = (combinations === binomial(nTarget, nTarget - clampedK)) ? 1 : 0;
        return {
            n: nTarget - getState(_state, 'n'),
            k: clampedK - getState(_state, 'k'),
            combinations: combinations - getState(_state, 'combinations'),
            pascalRow: pascalRow - getState(_state, 'pascalRow'),
            symmetry: symmetry - getState(_state, 'symmetry'),
        };
    },
    bounds: {
        n: { min: 1, max: 20 },
        combinations: { min: 1, max: 15504 },
    },
});
// ============================================================================
// RECURRENCE RELATION SOLVING
// ============================================================================
export const RecurrenceRelationSolving = createExperimentFromSchema({
    id: 'recurrence-relation-solving',
    name: 'Recurrence Relation Solving',
    domain: 'mathematics',
    description: 'Solve and analyze linear recurrence relations',
    conceptTags: ['Recurrence Relations', 'Closed Form', 'Characteristic Equation', 'Homogeneous'],
    difficulty: 6,
    stateVariables: [
        { id: 'n', name: 'Index n', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'an', name: 'aₙ recursive', unit: 'dimensionless', initial: 2, symbol: 'a_n' },
        { id: 'anClosed', name: 'aₙ closed', unit: 'dimensionless', initial: 2, symbol: 'a_n\\text{(closed)}' },
        { id: 'difference', name: '|recursive - closed|', unit: 'dimensionless', initial: 0, symbol: 'Error' },
        { id: 'homogeneous', name: 'Is Homogeneous', unit: 'dimensionless', initial: 1, symbol: 'H' },
        { id: 'order', name: 'Order', unit: 'dimensionless', initial: 2, symbol: 'k' },
    ],
    parameters: [
        { id: 'recurrenceType', name: 'Recurrence Type', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'R' },
        { id: 'a1', name: 'a₁', unit: 'dimensionless', min: 0, max: 10, default: 1, symbol: 'a_1' },
        { id: 'a2', name: 'a₂', unit: 'dimensionless', min: 0, max: 10, default: 1, symbol: 'a_2' },
    ],
    equations: [
        {
            id: 'linear-recurrence',
            name: 'Linear Recurrence',
            latex: 'a_n = c_1a_{n-1} + c_2a_{n-2} + \\cdots + c_ka_{n-k}',
            description: 'Each term is linear combination of previous terms',
        },
        {
            id: 'characteristic-eq',
            name: 'Characteristic Equation',
            latex: 'r^k - c_1r^{k-1} - \\cdots - c_k = 0',
            description: 'Find roots to determine closed form',
        },
    ],
    derivatives: (state, params) => {
        const recurrenceType = Math.round(getParam(params, 'recurrenceType'));
        const a1 = getParam(params, 'a1');
        const a2 = getParam(params, 'a2');
        const n = getState(state, 'n');
        const nextN = n + 1;
        let an = 0;
        let anClosed = 0;
        let homogeneous = 1;
        let order = 2;
        switch (recurrenceType) {
            case 0: // Fibonacci: aₙ = aₙ₋₁ + aₙ₋₂
                const fib = [0, 1];
                for (let i = 2; i <= nextN; i++) {
                    fib[i] = (fib[i - 1] ?? 0) + (fib[i - 2] ?? 0);
                }
                an = fib[nextN] ?? 0;
                // Closed form: Binet's formula
                const phi = (1 + Math.sqrt(5)) / 2;
                const psi = (1 - Math.sqrt(5)) / 2;
                anClosed = (Math.pow(phi, nextN) - Math.pow(psi, nextN)) / Math.sqrt(5);
                anClosed = Math.round(anClosed * 1000) / 1000;
                break;
            case 1: // aₙ = 2aₙ₋₁
                an = a1 * Math.pow(2, nextN - 1);
                anClosed = a1 * Math.pow(2, nextN - 1);
                order = 1;
                break;
            case 2: // aₙ = aₙ₋₁ + 2 (non-homogeneous)
                an = a1 + 2 * (nextN - 1);
                anClosed = a1 + 2 * (nextN - 1);
                homogeneous = 0;
                order = 1;
                break;
            case 3: // aₙ = 2aₙ₋₁ - aₙ₋₂ (repeated root)
                an = a1 + (a2 - a1) * (nextN - 1);
                anClosed = a1 + (a2 - a1) * (nextN - 1);
                break;
        }
        const difference = Math.abs(an - anClosed);
        return {
            n: 1,
            an: an - getState(state, 'an'),
            anClosed: anClosed - getState(state, 'anClosed'),
            difference: difference - getState(state, 'difference'),
            homogeneous: homogeneous - getState(state, 'homogeneous'),
            order: order - getState(state, 'order'),
        };
    },
    bounds: {
        n: { min: 1, max: 50 },
        an: { min: -100, max: 1e10 },
    },
});
// ============================================================================
// PIGEONHOLE PRINCIPLE APPLICATION
// ============================================================================
export const PigeonholePrincipleApplication = createExperimentFromSchema({
    id: 'pigeonhole-principle-application',
    name: 'Pigeonhole Principle Application',
    domain: 'mathematics',
    description: 'Explore the pigeonhole principle and collision forcing',
    conceptTags: ['Pigeonhole Principle', 'Combinatorics', 'Collision', 'Guarantees'],
    difficulty: 4,
    stateVariables: [
        { id: 'pigeons', name: 'Pigeons n', unit: 'dimensionless', initial: 13, symbol: 'n' },
        { id: 'holes', name: 'Holes m', unit: 'dimensionless', initial: 12, symbol: 'm' },
        { id: 'minCollision', name: 'Min Collision', unit: 'dimensionless', initial: 2, symbol: 'k' },
        { id: 'guaranteed', name: 'Collision Guaranteed', unit: 'dimensionless', initial: 1, symbol: 'G' },
        { id: 'ceiling', name: '⌈n/m⌉', unit: 'dimensionless', initial: 2, symbol: '\\lceil n/m \\rceil' },
    ],
    parameters: [
        { id: 'nPigeons', name: 'Items n', unit: 'dimensionless', min: 1, max: 100, default: 13, symbol: 'n' },
        { id: 'mHoles', name: 'Containers m', unit: 'dimensionless', min: 1, max: 20, default: 12, symbol: 'm' },
    ],
    equations: [
        {
            id: 'pigeonhole-basic',
            name: 'Basic Pigeonhole Principle',
            latex: 'n > m \\implies \\text{at least one container has } \\geq 2 \\text{ items}',
            description: 'More items than containers guarantees collision',
        },
        {
            id: 'pigeonhole-general',
            name: 'Generalized Pigeonhole Principle',
            latex: '\\lceil n/m \\rceil \\text{ items in some container}',
            description: 'Minimum guaranteed in one container',
        },
        {
            id: 'birthday-problem',
            name: 'Birthday Problem',
            latex: 'P(\\text{collision}) = 1 - \\frac{365!}{365^n(365-n)!}',
            description: 'Probability of shared birthday in n people',
            variables: ['pigeons'],
        },
    ],
    derivatives: (_state, params) => {
        const n = Math.round(getParam(params, 'nPigeons'));
        const m = Math.round(getParam(params, 'mHoles'));
        const minCollision = Math.ceil(n / m);
        const guaranteed = n > m ? 1 : 0;
        const ceiling = Math.ceil(n / m);
        return {
            pigeons: n - getState(_state, 'pigeons'),
            holes: m - getState(_state, 'holes'),
            minCollision: minCollision - getState(_state, 'minCollision'),
            guaranteed: guaranteed - getState(_state, 'guaranteed'),
            ceiling: ceiling - getState(_state, 'ceiling'),
        };
    },
    bounds: {
        pigeons: { min: 1, max: 100 },
        holes: { min: 1, max: 20 },
        minCollision: { min: 1, max: 10 },
    },
});
// ============================================================================
// INCLUSION EXCLUSION COUNTING
// ============================================================================
export const InclusionExclusionCounting = createExperimentFromSchema({
    id: 'inclusion-exclusion-counting',
    name: 'Inclusion-Exclusion Counting',
    domain: 'mathematics',
    description: 'Count elements in set unions using inclusion-exclusion principle',
    conceptTags: ['Inclusion-Exclusion', 'Set Theory', 'Counting', 'Venn Diagrams'],
    difficulty: 5,
    stateVariables: [
        { id: 'a', name: '|A|', unit: 'dimensionless', initial: 10, symbol: '|A|' },
        { id: 'b', name: '|B|', unit: 'dimensionless', initial: 15, symbol: '|B|' },
        { id: 'intersection', name: '|A ∩ B|', unit: 'dimensionless', initial: 5, symbol: '|A \\cap B|' },
        { id: 'union', name: '|A ∪ B|', unit: 'dimensionless', initial: 20, symbol: '|A \\cup B|' },
        { id: 'onlyA', name: 'A only', unit: 'dimensionless', initial: 5, symbol: '|A \\setminus B|' },
        { id: 'onlyB', name: 'B only', unit: 'dimensionless', initial: 10, symbol: '|B \\setminus A|' },
    ],
    parameters: [
        { id: 'sizeA', name: 'Set A size', unit: 'dimensionless', min: 0, max: 50, default: 10, symbol: '|A|' },
        { id: 'sizeB', name: 'Set B size', unit: 'dimensionless', min: 0, max: 50, default: 15, symbol: '|B|' },
        { id: 'sizeAB', name: 'Intersection', unit: 'dimensionless', min: 0, max: 20, default: 5, symbol: '|A \\cap B|' },
    ],
    equations: [
        {
            id: 'inclusion-exclusion-2',
            name: 'Two-Set Inclusion-Exclusion',
            latex: '|A \\cup B| = |A| + |B| - |A \\cap B|',
            description: 'Add sizes, subtract overlap counted twice',
            variables: ['a', 'b', 'intersection'],
        },
        {
            id: 'inclusion-exclusion-3',
            name: 'Three-Set Inclusion-Exclusion',
            latex: '|A \\cup B \\cup C| = |A|+|B|+|C| - |A\\cap B|-|A\\cap C|-|B\\cap C| + |A\\cap B\\cap C|',
            description: 'Alternate adding and subtracting intersections',
        },
        {
            id: 'difference',
            name: 'Set Difference',
            latex: '|A \\setminus B| = |A| - |A \\cap B|',
            description: 'Elements in A but not in B',
            variables: ['a', 'intersection'],
        },
    ],
    derivatives: (_state, params) => {
        const sizeA = getParam(params, 'sizeA');
        const sizeB = getParam(params, 'sizeB');
        const sizeAB = getParam(params, 'sizeAB');
        // Clamp intersection to minimum of set sizes
        const clampedAB = Math.min(sizeAB, sizeA, sizeB);
        const union = sizeA + sizeB - clampedAB;
        const onlyA = sizeA - clampedAB;
        const onlyB = sizeB - clampedAB;
        return {
            a: sizeA - getState(_state, 'a'),
            b: sizeB - getState(_state, 'b'),
            intersection: clampedAB - getState(_state, 'intersection'),
            union: union - getState(_state, 'union'),
            onlyA: onlyA - getState(_state, 'onlyA'),
            onlyB: onlyB - getState(_state, 'onlyB'),
        };
    },
    bounds: {
        a: { min: 0, max: 50 },
        b: { min: 0, max: 50 },
        union: { min: 0, max: 100 },
    },
});
// ============================================================================
// BOOLEAN ALGEBRA SIMPLIFICATION
// ============================================================================
export const BooleanAlgebraSimplification = createExperimentFromSchema({
    id: 'boolean-algebra-simplification',
    name: 'Boolean Algebra Simplification',
    domain: 'mathematics',
    description: 'Simplify Boolean expressions using algebraic laws',
    conceptTags: ['Boolean Algebra', 'Logic Gates', 'Karnaugh Maps', 'Minimization'],
    difficulty: 5,
    stateVariables: [
        { id: 'a', name: 'Input A', unit: 'dimensionless', initial: 1, symbol: 'A' },
        { id: 'b', name: 'Input B', unit: 'dimensionless', initial: 1, symbol: 'B' },
        { id: 'c', name: 'Input C', unit: 'dimensionless', initial: 0, symbol: 'C' },
        { id: 'original', name: 'Original F', unit: 'dimensionless', initial: 1, symbol: 'F_{orig}' },
        { id: 'simplified', name: 'Simplified F', unit: 'dimensionless', initial: 1, symbol: 'F_{simp}' },
        { id: 'gates', name: 'Gate Count', unit: 'dimensionless', initial: 3, symbol: 'N' },
    ],
    parameters: [
        { id: 'expressionType', name: 'Expression', unit: 'dimensionless', min: 0, max: 4, default: 0, symbol: 'Type' },
        { id: 'inputA', name: 'A value', unit: 'dimensionless', min: 0, max: 1, default: 1, symbol: 'A' },
        { id: 'inputB', name: 'B value', unit: 'dimensionless', min: 0, max: 1, default: 1, symbol: 'B' },
        { id: 'inputC', name: 'C value', unit: 'dimensionless', min: 0, max: 1, default: 0, symbol: 'C' },
    ],
    equations: [
        {
            id: 'identity',
            name: 'Identity Law',
            latex: 'A \\cdot 1 = A, \\quad A + 0 = A',
            description: 'AND with 1, OR with 0 leaves value unchanged',
        },
        {
            id: 'idempotent',
            name: 'Idempotent Law',
            latex: 'A \\cdot A = A, \\quad A + A = A',
            description: 'Operation on itself gives itself',
        },
        {
            id: 'complement',
            name: 'Complement Law',
            latex: 'A \\cdot \\overline{A} = 0, \\quad A + \\overline{A} = 1',
            description: 'Value ANDed with NOT is 0, ORed with NOT is 1',
        },
        {
            id: 'distributive',
            name: 'Distributive Law',
            latex: 'A(B + C) = AB + AC',
            description: 'AND distributes over OR',
        },
        {
            id: 'demorgan',
            name: "De Morgan's Law",
            latex: '\\overline{AB} = \\overline{A} + \\overline{B}, \\quad \\overline{A + B} = \\overline{A} \\cdot \\overline{B}',
            description: 'NOT of AND/OR equals OR/AND of NOTs',
        },
        {
            id: 'absorption',
            name: 'Absorption Law',
            latex: 'A + AB = A, \\quad A(A + B) = A',
            description: 'Term absorbs its superset',
        },
    ],
    derivatives: (_state, params) => {
        const exprType = Math.round(getParam(params, 'expressionType'));
        const A = Math.round(getParam(params, 'inputA'));
        const B = Math.round(getParam(params, 'inputB'));
        const C = Math.round(getParam(params, 'inputC'));
        const notA = A ? 0 : 1;
        const notB = B ? 0 : 1;
        const notC = C ? 0 : 1;
        let original = 0;
        let simplified = 0;
        let gates = 0;
        switch (exprType) {
            case 0: // AB + AB' = A (absorption)
                original = (A && B) || (A && notB) ? 1 : 0;
                simplified = A;
                gates = 4; // AB: 2, AB': 3, +: 4
                break;
            case 1: // A + A'B = A + B
                original = A || (notA && B) ? 1 : 0;
                simplified = A || B ? 1 : 0;
                gates = 4;
                break;
            case 2: // (A + B)(A + C) = A + BC
                original = (A || B) && (A || C) ? 1 : 0;
                simplified = A || (B && C) ? 1 : 0;
                gates = 5;
                break;
            case 3: // AB + AC + B'C' (Karnaugh simplification)
                original = (A && B) || (A && C) || (notB && notC) ? 1 : 0;
                // This doesn't simplify nicely, so show equivalence
                simplified = original;
                gates = 7;
                break;
            case 4: // A' + AB = A' + B
                original = notA || (A && B) ? 1 : 0;
                simplified = notA || B ? 1 : 0;
                gates = 4;
                break;
        }
        return {
            a: A - getState(_state, 'a'),
            b: B - getState(_state, 'b'),
            c: C - getState(_state, 'c'),
            original: original - getState(_state, 'original'),
            simplified: simplified - getState(_state, 'simplified'),
            gates: gates - getState(_state, 'gates'),
        };
    },
    bounds: {
        a: { min: 0, max: 1 },
        b: { min: 0, max: 1 },
        c: { min: 0, max: 1 },
        gates: { min: 1, max: 10 },
    },
});
// ============================================================================
// EXPORT ALL DISCRETE MATH EXPERIMENTS
// ============================================================================
export const ALL_DISCRETE_MATH = [
    CountingPermutations,
    CombinationSelection,
    RecurrenceRelationSolving,
    PigeonholePrincipleApplication,
    InclusionExclusionCounting,
    BooleanAlgebraSimplification,
];
//# sourceMappingURL=discrete-math.js.map